// Story 7.16 (ADO-1373), moved out of `Popover` by Story 11.10 (ADO-1961, the margin info panel)
// so the Info Panel places itself the same way: a panel portalled to `<body>`, `position: fixed`,
// placed below its trigger from the trigger's rect.
//
// ── Placement ──
// Below the trigger, flipping above when there is not room below and clamped into the viewport
// either way. Horizontally, `start` aligns the panel's left edge to the trigger's (the Popover);
// `startOrEnd` does too, but aligns its right edge to the trigger's when the left-aligned panel
// would run off the screen (the Info Panel, frame 00: "aligned to the button's left edge, or right
// edge near the screen edge"). Re-measured on scroll (capturing, so scrolls in any ancestor count),
// on resize, and when the panel's own box changes.

import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/** Gap between the trigger and the panel, and the minimum inset from any viewport edge. One
 *  value because both designs use one (8px). */
export const ANCHORED_PANEL_GAP = 8;

export type AnchoredAlignment = 'start' | 'startOrEnd';

export interface AnchoredPosition {
  readonly top: number;
  readonly left: number;
}

/**
 * Where an open panel sits against its trigger, or `null` until it has been measured (and again
 * once it closes, so the next open measures fresh rather than painting one frame at the previous
 * trigger's coordinates).
 */
export function useAnchoredPlacement(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  alignment: AnchoredAlignment = 'start',
): AnchoredPosition | null {
  const [position, setPosition] = useState<AnchoredPosition | null>(null);

  // Measured in a LAYOUT effect so the panel is placed before the browser paints; until it has a
  // position the caller keeps it `visibility: hidden`, so it never flashes at the top-left of the
  // viewport on the way to its real home.
  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (anchor === null || panel === null) return;
      const trigger = anchor.getBoundingClientRect();
      const box = panel.getBoundingClientRect();

      const maxLeft = window.innerWidth - box.width - ANCHORED_PANEL_GAP;
      const preferred =
        alignment === 'startOrEnd' && trigger.left > maxLeft
          ? trigger.right - box.width
          : trigger.left;
      const left = Math.max(ANCHORED_PANEL_GAP, Math.min(preferred, maxLeft));

      const below = trigger.bottom + ANCHORED_PANEL_GAP;
      const above = trigger.top - box.height - ANCHORED_PANEL_GAP;
      // Prefer below; flip above only when below overflows AND above actually fits, so a panel
      // taller than the viewport is not flipped into a worse position. The final clamp keeps the
      // top edge reachable either way.
      const fitsBelow = below + box.height <= window.innerHeight - ANCHORED_PANEL_GAP;
      const top = fitsBelow ? below : above >= ANCHORED_PANEL_GAP ? above : ANCHORED_PANEL_GAP;

      setPosition({ top, left });
    };
    reposition();
    // Capturing, so a scroll in ANY ancestor (the listing's own scroll container, not just the
    // window) re-places the panel rather than leaving it behind.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    // PR #333 review (@lozzo27): `StockCardContent` fetches asynchronously and grows the panel
    // AFTER this initial measurement. In the flip-above branch `top` is computed from the panel's
    // small initial (loading) height, so once the real content lands the panel keeps that fixed
    // top and extends DOWNWARD over the trigger. A `ResizeObserver` on the panel itself re-runs
    // `reposition` whenever its box actually changes, not just on scroll/resize/open.
    const panel = panelRef.current;
    const resizeObserver = panel && new ResizeObserver(reposition);
    if (resizeObserver && panel) resizeObserver.observe(panel);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      resizeObserver?.disconnect();
    };
  }, [open, anchorRef, panelRef, alignment]);

  useEffect(() => {
    if (!open) setPosition(null);
  }, [open]);

  return position;
}

/**
 * Close an open non-modal panel on Escape or a press outside it. Both live on the DOCUMENT rather
 * than on the panel, so both still dismiss when focus is outside it (a click on the page, a Tab
 * out, or the Info Panel, which keeps focus on its button). A press on the trigger does NOT close:
 * the trigger owns the open/close toggle, and closing on ITS press would be undone by its own
 * click handler reopening the panel a moment later.
 *
 * The press is `pointerdown`, which fires for a mouse, a finger and a pen alike (Story 11.10,
 * MI-25): iOS Safari does not reliably fire `mousedown` for a tap on something that is not
 * clickable, so a tap outside on an iPad would not close the panel.
 */
export function useDismissAnchoredPanel(
  open: boolean,
  onClose: () => void,
  anchorRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (panelRef.current?.contains(target) === true) return;
      if (anchorRef.current?.contains(target) === true) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, onClose, anchorRef, panelRef]);
}
